import { useCallback, useState } from 'react';
import { useFormikContext } from 'formik';

import EuroIcon from '@mui/icons-material/Euro';
import type { GridRowId } from '@mui/x-data-grid';
import type { GridRowSelectionModel } from '@mui/x-data-grid';

import { ensureDefined } from '@e-socna/utils';

import { SpeedDial } from '../../../../components/SpeedDial';
import { RightDrawer } from '../../../../components/RightDrawer';
import { useActionBlocker } from '../../../../hooks/useActionBlocker.hook';
import { useReferencesContext } from '../../../../contexts/ReferencesContext/hooks/useReferencesContext.hook';
import { CostList } from '../../../../components/CostList';
import { useIdFactory } from '../../../../hooks/useIdFactory.hook';
import { useFindAllCostsQuery } from '../../../../components/CostList/hooks/useFindAllCostsQuery.hook';
import { renumber } from '../../../../utils/renumber.util';

import { FormVariant } from '../../../enums/variant.enum';
import { CostForm } from '../../../Cost';
import type { CostMutationResult } from '../../../Cost/hooks/useSubmit.hook';

import type { CostUsageForm_CostUsage } from '../../fragments/cost-usage.fragment';

interface Props {
  readonly costUsages: readonly CostUsageForm_CostUsage[];
  readonly fieldName: string;
}

// Fills the scrolling wizard step so the data grid scrolls its rows instead of
// growing with them; below 300px the step itself scrolls.
const EMBEDDED_LIST_SX = {
  boxSizing: 'border-box',
  height: '100%',
  minHeight: 300,
  pt: 3,
  pb: 1,
};

export const Selection = ({ costUsages, fieldName }: Readonly<Props>) => {
  const { setFieldValue } = useFormikContext();

  const referencesContext = useReferencesContext();

  const { defaultUnit } = ensureDefined({
    defaultUnit: referencesContext.defaultUnit,
  });

  const { data } = useFindAllCostsQuery();
  const allCosts = data?.findAllCosts;

  const { generateId } = useIdFactory();

  const handleRowSelectionModelChange = useCallback(
    (selection: Readonly<GridRowSelectionModel>) => {
      const newIds = [...selection.ids] as readonly GridRowId[];
      const currentIdsSet = new Set<GridRowId>(
        costUsages.map((usage) => usage.costId),
      );
      const newIdsSet = new Set<GridRowId>(newIds);

      const kept = costUsages.filter((usage) => newIdsSet.has(usage.costId));

      const idsToAdd = newIds.filter((id) => !currentIdsSet.has(id));

      const added = idsToAdd.map((id, i) => {
        const { costUsed } = ensureDefined({
          costUsed: allCosts?.find((cost) => cost._id === id),
        });

        return {
          __typename: 'CostUsage' as const,
          _id: generateId(),
          costId: costUsed._id,
          quantity: {
            __typename: 'Quantity' as const,
            unitId: defaultUnit._id,
            value: 1,
          },
          index: kept.length + i,
        };
      });

      void setFieldValue(fieldName, renumber([...kept, ...added]));
    },
    [
      allCosts,
      costUsages,
      defaultUnit._id,
      fieldName,
      generateId,
      setFieldValue,
    ],
  );

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const openDrawer = useCallback(() => {
    setIsDrawerOpen(true);
  }, []);
  const closeDrawer = useCallback(() => {
    setIsDrawerOpen(false);
  }, []);

  const handleSuccess = useCallback(
    (result: CostMutationResult) => {
      setIsDrawerOpen(false);

      const { id } = ensureDefined({
        id:
          'createFixedCost' in result
            ? result.createFixedCost._id
            : 'createVariableCost' in result
              ? result.createVariableCost._id
              : 'findOneAndUpdateFixedCost' in result
                ? result.findOneAndUpdateFixedCost._id
                : 'findOneAndUpdateVariableCost' in result
                  ? result.findOneAndUpdateVariableCost._id
                  : undefined,
      });

      const newUsage: CostUsageForm_CostUsage = {
        __typename: 'CostUsage' as const,
        _id: generateId(),
        costId: id,
        quantity: {
          __typename: 'Quantity' as const,
          unitId: defaultUnit._id,
          value: 1,
        },
        index: costUsages.length,
      };

      void setFieldValue(fieldName, renumber([...costUsages, newUsage]));
    },
    [costUsages, defaultUnit._id, fieldName, generateId, setFieldValue],
  );

  const [blockableCloseDrawer, setMustBlockAction] =
    useActionBlocker(closeDrawer);

  return (
    <>
      <CostList
        checkboxSelection
        onRowSelectionModelChange={handleRowSelectionModelChange}
        rowSelectionModel={{
          type: 'include',
          ids: new Set(costUsages.map((costUsage) => costUsage.costId)),
        }}
        sx={EMBEDDED_LIST_SX}
      />
      <SpeedDial
        actions={[
          {
            id: 'createCost',
            icon: <EuroIcon />,
            name: 'Créer Coût',
            onClick: openDrawer,
          },
        ]}
        title="Actions possibles sur les Coûts"
      />
      <RightDrawer isOpen={isDrawerOpen} onDrawerClose={blockableCloseDrawer}>
        <CostForm
          formVariant={FormVariant.Creation}
          onCancel={blockableCloseDrawer}
          onSuccess={handleSuccess}
          setMustBlockAction={setMustBlockAction}
          showCancelButton
          title="Formulaire de création d'un Coût"
        />
      </RightDrawer>
    </>
  );
};
